import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Grid,
  Card,
  CardContent,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Alert,
  CircularProgress,
  Button
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  People as PeopleIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Home as AddressIcon,
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon
} from '@mui/icons-material';
import AdminPageLayout from '../components/admin/AdminPageLayout';
import CustomerDetailsDialog from '../components/admin/CustomerDetailsDialog';
import AddCustomerDialog from '../components/admin/AddCustomerDialog';
import ScheduleJobDialog from '../components/admin/ScheduleJobDialog';

const CustomerManagement = () => {
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [cityFilter, setCityFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [scheduleFor, setScheduleFor] = useState(null); // customer row to schedule a job for
  const [overviewRefreshKey, setOverviewRefreshKey] = useState(0);

  const apiBase = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    filterCustomers();
  }, [customers, searchQuery, cityFilter, statusFilter]);

  // Deep-link support: /customer-management?customerId=<id> auto-opens that
  // customer's details dialog once the list has loaded (e.g. from the map).
  useEffect(() => {
    const customerId = searchParams.get('customerId');
    if (customerId && customers.length > 0) {
      const match = customers.find((c) => String(c.id) === String(customerId));
      if (match) {
        handleViewDetails(match);
        searchParams.delete('customerId');
        setSearchParams(searchParams, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customers, searchParams]);

  const fetchCustomers = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${apiBase}/api/users/clients`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch customers');
      }

      const data = await response.json();
      setCustomers(data);
    } catch (err) {
      setError(err.message || 'Failed to fetch customers');
    } finally {
      setLoading(false);
    }
  };

  const filterCustomers = () => {
    let filtered = [...customers];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(customer =>
        customer.firstName?.toLowerCase().includes(query) ||
        customer.lastName?.toLowerCase().includes(query) ||
        customer.email?.toLowerCase().includes(query) ||
        customer.fullName?.toLowerCase().includes(query)
      );
    }

    // City filter
    if (cityFilter !== 'all') {
      filtered = filtered.filter(customer => customer.city === cityFilter);
    }

    // Status filter
    if (statusFilter === 'active') {
      filtered = filtered.filter(customer => customer.isActive === true);
    } else if (statusFilter === 'inactive') {
      filtered = filtered.filter(customer => customer.isActive === false);
    }

    setFilteredCustomers(filtered);
  };

  const handleViewDetails = (customer) => {
    setSelectedCustomer(customer);
    setDetailsDialogOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsDialogOpen(false);
    setSelectedCustomer(null);
  };

  const handleCustomerUpdate = () => {
    // Refresh customers after update
    fetchCustomers();
  };

  const handleAddCustomer = () => {
    setAddDialogOpen(true);
  };

  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
  };

  const handleCustomerAdded = () => {
    // Refresh customers after adding new customer
    fetchCustomers();
  };

  const handleDeleteCustomer = async (customer, event) => {
    // Prevent row click event
    event.stopPropagation();

    const customerName = `${customer.firstName} ${customer.lastName}`;

    if (!window.confirm(
      `Are you sure you want to delete customer "${customerName}"?\n\n` +
      `This will permanently delete:\n` +
      `- The user account\n` +
      `- Customer profile\n` +
      `- All associated data (bookings, jobs, etc.)\n\n` +
      `This action cannot be undone!`
    )) {
      return;
    }

    try {
      const response = await fetch(`${apiBase}/api/users/delete/${customer.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete customer');
      }

      // Refresh customer list
      fetchCustomers();

    } catch (err) {
      setError(err.message || 'Failed to delete customer');
    }
  };

  // Get unique cities for filter
  const uniqueCities = [...new Set(customers.map(c => c.city).filter(Boolean))].sort();

  // Stats calculations
  const totalCustomers = customers.length;
  const activeCustomers = customers.filter(c => c.isActive).length;
  const customersWithAddress = customers.filter(c => c.address).length;

  // DataGrid columns
  const columns = [
    {
      field: 'fullName',
      headerName: 'Name',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight="medium">
            {params.value || `${params.row.firstName} ${params.row.lastName}`}
          </Typography>
        </Box>
      )
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      minWidth: 200
    },
    {
      field: 'phoneNumber',
      headerName: 'Phone',
      flex: 0.8,
      minWidth: 130
    },
    {
      field: 'address',
      headerName: 'Address',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Typography variant="body2" noWrap>
          {params.value || '-'}
        </Typography>
      )
    },
    {
      field: 'city',
      headerName: 'City',
      flex: 0.8,
      minWidth: 120
    },
    {
      field: 'state',
      headerName: 'State',
      flex: 0.5,
      minWidth: 80
    },
    {
      field: 'isActive',
      headerName: 'Status',
      flex: 0.6,
      minWidth: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'default'}
          size="small"
          icon={params.value ? <ActiveIcon /> : <InactiveIcon />}
        />
      )
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      flex: 0.8,
      minWidth: 110,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value ? new Date(params.value).toLocaleDateString() : '-'}
        </Typography>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.7,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => (
        <Box display="flex" gap={0.5}>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              onClick={() => handleViewDetails(params.row)}
              color="primary"
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Customer">
            <IconButton
              size="small"
              onClick={(e) => handleDeleteCustomer(params.row, e)}
              color="error"
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  return (
    <AdminPageLayout>
        <Box sx={{ mb: 4 }}>
          {/* Header with Add Customer Button */}
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
            <Typography variant="h4" component="h1" fontWeight="bold">
              Customer Management
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<PersonAddIcon />}
              onClick={handleAddCustomer}
              size="large"
            >
              Add Customer
            </Button>
          </Box>

          {/* Stats Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2}>
                    <PeopleIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                    <Box>
                      <Typography variant="h4" color="primary">
                        {totalCustomers}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Customers
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2}>
                    <ActiveIcon sx={{ fontSize: 40, color: 'success.main' }} />
                    <Box>
                      <Typography variant="h4" color="success.main">
                        {activeCustomers}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Active Customers
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2}>
                    <AddressIcon sx={{ fontSize: 40, color: 'info.main' }} />
                    <Box>
                      <Typography variant="h4" color="info.main">
                        {customersWithAddress}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Complete Profiles
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Filters and Search */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>City</InputLabel>
                  <Select
                    value={cityFilter}
                    onChange={(e) => setCityFilter(e.target.value)}
                    label="City"
                  >
                    <MenuItem value="all">All Cities</MenuItem>
                    {uniqueCities.map(city => (
                      <MenuItem key={city} value={city}>{city}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={1}>
                <Tooltip title="Refresh">
                  <IconButton
                    onClick={fetchCustomers}
                    disabled={loading}
                    color="primary"
                    sx={{ width: '100%' }}
                  >
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          </Paper>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {/* Data Grid */}
          <Paper sx={{ p: 2 }}>
            <Box sx={{ height: 600, width: '100%' }}>
              <DataGrid
                rows={filteredCustomers}
                columns={columns}
                pageSize={10}
                rowsPerPageOptions={[10, 25, 50, 100]}
                disableSelectionOnClick
                loading={loading}
                components={{
                  LoadingOverlay: () => (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%'
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  ),
                  NoRowsOverlay: () => (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                        gap: 2
                      }}
                    >
                      <PeopleIcon sx={{ fontSize: 60, color: 'text.secondary' }} />
                      <Typography variant="h6" color="text.secondary">
                        No customers found
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {searchQuery || cityFilter !== 'all' || statusFilter !== 'all'
                          ? 'Try adjusting your filters'
                          : 'No customers have been added yet'}
                      </Typography>
                    </Box>
                  )
                }}
                sx={{
                  '& .MuiDataGrid-cell:focus': {
                    outline: 'none'
                  },
                  '& .MuiDataGrid-row:hover': {
                    cursor: 'pointer'
                  }
                }}
              />
            </Box>
          </Paper>
        </Box>

        {/* Customer Details Dialog */}
        <CustomerDetailsDialog
          open={detailsDialogOpen}
          onClose={handleCloseDetails}
          customer={selectedCustomer}
          onUpdate={handleCustomerUpdate}
          onScheduleJob={(customer) => setScheduleFor(customer)}
          refreshKey={overviewRefreshKey}
        />

        {/* Schedule a job for the customer currently open in the details dialog */}
        <ScheduleJobDialog
          open={!!scheduleFor}
          onClose={() => setScheduleFor(null)}
          initialClientProfileId={scheduleFor?.clientProfile?.id || null}
          onCreated={() => setOverviewRefreshKey((k) => k + 1)}
        />

        {/* Add Customer Dialog */}
        <AddCustomerDialog
          open={addDialogOpen}
          onClose={handleCloseAddDialog}
          onCustomerAdded={handleCustomerAdded}
        />
    </AdminPageLayout>
  );
};

export default CustomerManagement;
